/**
 * useCircleId — Resolves a circle URL slug to a Firestore circle ID.
 *
 * Priority:
 *  1. Router state (passed during internal navigation)
 *  2. Slug resolution via public circles API (direct URL / SEO)
 *  3. The param itself, when it is shaped like a Firestore id (PPL-531)
 *
 * Step 3 is what makes a link built from a notification work. A rail entry
 * carries `circleId` and nothing else — no name, so no slug — and a tray tap
 * navigates by URL with no router state to ride along. It also rescues PRIVATE
 * circles, which step 2 can never resolve: `resolveCircleSlug` matches against
 * the PUBLIC circles list, so a private circle's own members could not reach it
 * by URL at all. Slug resolution still runs first, so a real slug that happens
 * to look like an id keeps winning.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveCircleSlug } from '../services/circleService';

/**
 * A Firestore auto-id: 20+ characters, letters and digits only. `slugify` emits
 * dashes between words, so a slug reaching this shape is a single very long
 * word — and it only gets here after slug resolution has already missed.
 */
function looksLikeCircleId(value: string): boolean {
  return /^[A-Za-z0-9]{20,}$/.test(value);
}

export function useCircleId(slugParam: string | undefined): {
  circleId: string | null;
  resolving: boolean;
} {
  const location = useLocation();
  const stateId = (location.state as any)?.circleId as string | undefined;

  const [resolvedId, setResolvedId] = useState<string | null>(stateId ?? null);
  const [resolving, setResolving] = useState(!stateId && !!slugParam);

  useEffect(() => {
    if (stateId) {
      setResolvedId(stateId);
      setResolving(false);
      return;
    }
    if (!slugParam) return;

    let cancelled = false;
    setResolving(true);
    resolveCircleSlug(slugParam).then((id) => {
      if (!cancelled) {
        setResolvedId(id ?? (looksLikeCircleId(slugParam) ? slugParam : null));
        setResolving(false);
      }
    });
    return () => { cancelled = true; };
  }, [slugParam, stateId]);

  return { circleId: resolvedId, resolving };
}
